/**
 * 404 页面：当用户访问不存在的路径时显示。
 * 采用简洁优雅的设计风格，带有动画效果。
 */

import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Home, ArrowLeft, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * 动画浮动装饰组件
 * @returns 装饰性动画背景
 */
function FloatingShapes(): ReactElement {
  return (
    <div className="absolute inset-0 overflow-hidden">
      {/* 主色调大圆 */}
      <motion.div
        className="absolute -left-20 top-20 h-72 w-72 rounded-full bg-primary/10 blur-3xl"
        animate={{
          x: [0, 30, 0],
          y: [0, -20, 0],
          scale: [1, 1.1, 1],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      {/* 紫色中圆 */}
      <motion.div
        className="absolute bottom-20 right-0 h-64 w-64 rounded-full bg-purple-500/10 blur-3xl"
        animate={{
          x: [0, -30, 0],
          y: [0, 20, 0],
          scale: [1, 1.15, 1],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
      />
      {/* 琥珀色小圆 */}
      <motion.div
        className="absolute right-1/4 top-1/4 h-40 w-40 rounded-full bg-amber-500/8 blur-3xl"
        animate={{
          x: [0, 40, 0],
          y: [0, -30, 0],
          scale: [1, 1.2, 1],
        }}
        transition={{
          duration: 12,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 2,
        }}
      />
    </div>
  )
}

/**
 * 404 大数字组件
 * @returns 动画数字
 */
function NotFoundNumber(): ReactElement {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20,
        delay: 0.1,
      }}
      className="relative"
    >
      <span className="text-[12rem] font-bold leading-none tracking-tighter text-foreground/5 sm:text-[16rem]">
        404
      </span>
      <motion.span
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="absolute inset-0 flex items-center justify-center text-[6rem] font-bold tracking-tighter text-foreground sm:text-[8rem]"
      >
        404
      </motion.span>
    </motion.div>
  )
}

/**
 * 404 页面组件
 * @returns 页面 ReactElement
 */
export function NotFoundPage(): ReactElement {
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-background px-4">
      {/* 动画背景 */}
      <FloatingShapes />

      {/* 主内容区 */}
      <div className="relative z-10 flex flex-col items-center text-center">
        {/* 404 数字 */}
        <NotFoundNumber />

        {/* 提示文案 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="-mt-8 space-y-4"
        >
          <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
            页面不见了
          </h1>
          <p className="max-w-md text-muted-foreground">
            抱歉，您访问的页面可能已被移除、名称已更改或暂时不可用。
          </p>
        </motion.div>

        {/* 操作按钮 */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8 flex flex-col gap-3 sm:flex-row sm:gap-4"
        >
          <Button asChild size="lg" className="gap-2">
            <Link to="/">
              <Home className="h-4 w-4" />
              返回首页
            </Link>
          </Button>
          <Button
            variant="outline"
            size="lg"
            className="gap-2"
            onClick={(): void => {
              window.history.back()
            }}
          >
            <ArrowLeft className="h-4 w-4" />
            返回上一页
          </Button>
        </motion.div>

        {/* 搜索建议 */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="mt-12 flex items-center gap-2 text-sm text-muted-foreground"
        >
          <Search className="h-4 w-4" />
          <span>或尝试使用导航菜单查找您需要的内容</span>
        </motion.div>
      </div>

      {/* 底部装饰线 */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ delay: 0.8, duration: 0.6 }}
        className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent"
      />
    </div>
  )
}
